/**
 * Locale 上下文与工具（06 开发文档 LocaleRouter/LanguageSwitcher）。
 * - locale 由路由 /{locale}/... 提取（LanguageRedirect 已保证进入时合法）
 * - 语言偏好记忆：localStorage('chronospeak-locale')，LanguageRedirect 读取
 * - 切换语言 = 同路径等价映射（LanguageSwitcher）
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { EventNameValue, TrackEventInput } from '@chronospeak/contracts';
import type { Locale } from '@chronospeak/contracts';
import type { LocaleMeta } from '@chronospeak/contracts';

export const LOCALE_STORAGE_KEY = 'chronospeak-locale';

export function readLocalePreference(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return v === 'zh-CN' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

export function writeLocalePreference(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // 隐私模式等场景静默失败
  }
}

interface LocaleContextValue {
  locale: Locale;
  /** 同路径切换语言（等价页面映射） */
  switchTo: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const switchTo = useCallback(
    (next: Locale) => {
      if (next === locale) return;
      const rest = location.pathname.replace(`/${locale}`, '') || '';
      const target = `/${next}${rest}`;
      writeLocalePreference(next);
      // language_changed 事件（09 冻结事件名；不记录 PII）
      track('language_changed', { from: locale, to: next, path: location.pathname });
      navigate(target, { replace: false });
    },
    [locale, location.pathname, navigate],
  );

  // HTML lang 与 hreflang/canonical（06 开发清单 + 08 SEO 装载）。
  // 一期 CSR：Google 渲染 JS 后可读（PRD §10 验收口径）；SSR/预渲染列后续迭代。
  // hreflang 指向同路径等价语言页；canonical 指向当前 locale 的规范 URL；
  // x-default = 域根（生产由 LanguageRedirect 按 localStorage 偏好重定向到具体语言）。
  useEffect(() => {
    document.documentElement.lang = locale === 'zh-CN' ? 'zh-CN' : 'en';
    const origin = window.location.origin;
    const rest = location.pathname.replace(`/${locale}`, '') || '';
    const head = document.head;
    const upsert = (rel: 'canonical' | 'alternate', hreflang: string | null, href: string) => {
      const selector = hreflang
        ? `link[rel="alternate"][hreflang="${hreflang}"]`
        : 'link[rel="canonical"]';
      let el = head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        if (hreflang) el.hreflang = hreflang;
        head.appendChild(el);
      }
      el.href = href;
    };
    upsert('canonical', null, `${origin}/${locale}${rest}${location.search}`);
    upsert('alternate', 'zh-CN', `${origin}/zh-CN${rest}${location.search}`);
    upsert('alternate', 'en', `${origin}/en${rest}${location.search}`);
    upsert('alternate', 'x-default', `${origin}/`);
  }, [locale, location.pathname, location.search]);

  const value = useMemo(() => ({ locale, switchTo }), [locale, switchTo]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale 必须在 LocaleProvider 内使用');
  return ctx;
}

/* ---------------- 埋点薄接口（09 开发文档 track(eventName, payload)） ---------------- */

const EVENT_ENDPOINT = '/api/v1/public/events';

/** 页面只传白名单字段（contracts eventPropertiesWhitelist），服务端兜底过滤 */
export function track(
  name: EventNameValue,
  properties?: Record<string, string | number | boolean>,
): void {
  const payload: TrackEventInput = {
    name,
    occurredAt: new Date().toISOString(),
    properties,
  };
  try {
    // 发后即忘；埋点失败不影响页面（决策 9：一期无监控，靠日志）
    void fetch(EVENT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // 静默
  }
}

/* ---------------- 动态内容信封工具（LocaleMeta 消费） ---------------- */

/** 从 API 信封提取元数据；组件用 useApiLocaleMeta 决定是否显示回退提示 */
export function localeMetaFrom<T extends LocaleMeta>(envelope: T): LocaleMeta {
  return {
    requestedLocale: envelope.requestedLocale,
    locale: envelope.locale,
    fallbackUsed: envelope.fallbackUsed,
  };
}

/**
 * 剧本/固定文案双语取值（zh|en 二元结构 → locale 键）。
 * contracts 的 q/ph/chips 用 { zh, en }；locale 用 'zh-CN'|'en'——此处统一桥接。
 */
export function pickZhEn<T>(pair: { zh: T; en: T }, locale: 'zh-CN' | 'en'): T {
  return locale === 'en' ? pair.en : pair.zh;
}

/**
 * 英文未审标注规则（06 开发清单 DraftBadge）：
 * - 信封 locale=en 且 fallbackUsed=false → 有英文；英文标题带「[机翻未审]」前缀时视为未审
 * - 服务端 translationStatus 不直接下发（公开投影不含）——由「[机翻未审]」前缀约定判定，
 *   reviewed 稿（Claude 审校后）去前缀，标注自动消失
 */
export function isUnreviewedEnglish(text: string): boolean {
  return text.includes('[机翻未审]') || text.includes('[Machine-translated');
}
